import { useState, useEffect, createContext, useContext, ReactNode, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { User as CustomUser } from '../types/database';

interface AuthContextType {
  user: User | null;
  userProfile: CustomUser | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: unknown }>;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: unknown }>;
  signOut: () => Promise<{ error: unknown }>;
  resetPassword: (email: string) => Promise<{ error: unknown }>;
  updateProfile: (updates: Partial<CustomUser>) => Promise<{ error: unknown }>;
  deactivateAccount: () => Promise<{ error: unknown }>;
  eraseAccount: () => Promise<{ error: unknown }>;
  reactivateAccount: () => Promise<{ error: unknown }>;
}


const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<CustomUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  // Id de l'utilisateur dont le chargement du profil est terminé (réussi ou non)
  const [profileResolvedFor, setProfileResolvedFor] = useState<string | null>(null);


  const createUserProfile = useCallback(async (authUser: User) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .insert({
          id: authUser.id,
          email: authUser.email!,
          full_name: authUser.user_metadata?.full_name || '',
        })
        .select()
        .single();

      if (!error && data) {
        setUserProfile(data);
      }
    } catch (error) {
      console.error('Erreur lors de la création du profil:', error);
    }
  }, [setUserProfile]);

  const loadUserProfile = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (error && (error as { code?: string }).code === 'PGRST116') {
        // L'utilisateur n'existe pas encore dans notre table custom
        // Créons-le automatiquement
        const { data: userData } = await supabase.auth.getUser();
        if (userData.user) {
          await createUserProfile(userData.user);
        }
      } else if (data) {
        setUserProfile(data);
        // Ne plus faire de redirection automatique ici
        // C'est le ProtectedRoute qui s'en chargera
      }
    } catch (error) {
      console.error('Erreur lors du chargement du profil:', error);
    } finally {
      setProfileResolvedFor(userId);
    }
  }, [createUserProfile]);

  useEffect(() => {
    // Récupérer la session initiale
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        loadUserProfile(session.user.id);
      }
      setLoading(false);
    });

    // Écouter les changements d'auth
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        // ✅ CORRECT: Utiliser setTimeout pour éviter les dead-locks
        setTimeout(() => {
          loadUserProfile(session.user.id);
        }, 0);
        // Après confirmation d'e-mail, c'est la page /auth/verify qui affiche le succès
        // puis redirige vers le dashboard.
      } else {
        setUserProfile(null);
      }

      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [loadUserProfile]);



  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName || '',
        },
        // ✅ CORRECT: Redirection vers la page de vérification d'email
        emailRedirectTo: `${window.location.origin}/auth/verify`,
      },
    });
    return { error };
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Erreur lors de la déconnexion:', error);
      }
      return { error };
    } catch (error) {
      console.error('Erreur inattendue lors de la déconnexion:', error);
      return { error };
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      // Note: Supabase ne révèle pas si l'email existe pour des raisons de sécurité
      // Le message de succès sera toujours affiché même si l'email n'existe pas
      return { error };
    } catch (error) {
      console.error('Erreur lors de la réinitialisation:', error);
      return { error };
    }
  };

  const updateProfile = async (updates: Partial<CustomUser>) => {
    if (!user) return { error: new Error('Utilisateur non connecté') };

    try {
      const { error } = await supabase
        .from('users')
        .update(updates)
        .eq('id', user.id);

      if (!error) {
        setUserProfile(prev => prev ? { ...prev, ...updates } : null);
      }

      return { error };
    } catch (error) {
      return { error };
    }
  };

  const deactivateAccount = async () => {
    if (!user) return { error: new Error('Utilisateur non connecté') };

    try {
      // Marquer le compte comme désactivé dans notre table custom
      const { error } = await supabase
        .from('users')
        .update({
          is_active: false,
          deactivated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) {
        console.error('Erreur lors de la désactivation du compte:', error);
        return { error };
      }

      // Déconnecter l'utilisateur après désactivation
      const { error: signOutError } = await supabase.auth.signOut();
      if (signOutError) {
        console.error('Erreur lors de la déconnexion après désactivation:', signOutError);
      }

      return { error: null };
    } catch (error) {
      console.error('Erreur lors de la désactivation du compte:', error);
      return { error };
    }
  };

  // Suppression définitive (RGPD) : l'Edge Function efface licences, activations,
  // journaux et profil, conserve les transactions sans lien avec la personne,
  // puis supprime le compte d'authentification.
  const eraseAccount = async () => {
    if (!user) return { error: new Error('Utilisateur non connecté') };

    try {
      const { error } = await supabase.functions.invoke('suppression-user', {
        body: { confirm: 'SUPPRIMER' },
      });

      if (error) {
        console.error('Erreur lors de la suppression du compte:', error);
        return { error };
      }

      // Le compte n'existe plus côté serveur : on purge seulement la session locale.
      await supabase.auth.signOut({ scope: 'local' });

      return { error: null };
    } catch (error) {
      console.error('Erreur lors de la suppression du compte:', error);
      return { error };
    }
  };

  const reactivateAccount = async () => {
    if (!user) return { error: new Error('Utilisateur non connecté') };

    try {
      // Réactiver le compte
      const { error } = await supabase
        .from('users')
        .update({
          is_active: true,
          deactivated_at: null
        })
        .eq('id', user.id);

      if (error) {
        console.error('Erreur lors de la réactivation du compte:', error);
        return { error };
      }

      // Mettre à jour le profil utilisateur local
      setUserProfile(prev => prev ? {
        ...prev,
        is_active: true,
        deactivated_at: undefined
      } : null);

      return { error: null };
    } catch (error) {
      console.error('Erreur lors de la réactivation du compte:', error);
      return { error };
    }
  };

  const value = {
    user,
    userProfile,
    session,
    // Tant que le profil d'un utilisateur connecté n'est pas résolu, on reste en chargement :
    // sinon un compte désactivé verrait brièvement le contenu protégé.
    loading: loading || (user !== null && profileResolvedFor !== user.id),
    signIn,
    signUp,
    signOut,
    resetPassword,
    updateProfile,
    deactivateAccount,
    eraseAccount,
    reactivateAccount,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
